import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { Layout } from '../../components/Layout/Layout';
import { ProductDto } from '../../types';
import styles from './EnrichisseurFournisseur.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FournisseurResult {
  name: string;
  image: string;
  imageLarge: string;
  url: string;
  price: string;
  description: string;
}

interface ProductEnrich {
  product: ProductDto;
  searching: boolean;
  searchError: string;
  results: FournisseurResult[];
  selected: FournisseurResult | null;
  richDescription: string;
  detailLoading: boolean;
  applyImage: boolean;
  applyDescription: boolean;
  applyPrixAchat: boolean;
  newPrixAchat: number;
}

interface ValidationResult {
  productId: number;
  success: boolean;
  message?: string;
  newImageUrl?: string;
}

type Phase = 'options' | 'recherche' | 'apercu' | 'validation';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'enrichisseur_products';

// ─── Component ────────────────────────────────────────────────────────────────

export const EnrichisseurFournisseur: React.FC = () => {
  const navigate = useNavigate();

  const [products] = useState<ProductDto[]>(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '[]'); } catch { return []; }
  });

  const [phase, setPhase] = useState<Phase>('options');
  const [optImage, setOptImage] = useState(true);
  const [optDescription, setOptDescription] = useState(true);
  const [optPrixAchat, setOptPrixAchat] = useState(false);

  const [enrichData, setEnrichData] = useState<ProductEnrich[]>([]);
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [validating, setValidating] = useState(false);

  // Redirige si pas de produits
  useEffect(() => {
    if (products.length === 0) navigate('/produits');
  }, [products, navigate]);

  const updateItem = useCallback((productId: number, updates: Partial<ProductEnrich>) => {
    setEnrichData(prev => prev.map(e => e.product.id === productId ? { ...e, ...updates } : e));
  }, []);

  // ── Phase recherche : lancer la recherche pour tous ──────────────────────

  const startRecherche = useCallback(async () => {
    const initial: ProductEnrich[] = products.map(p => ({
      product: p,
      searching: true,
      searchError: '',
      results: [],
      selected: null,
      richDescription: '',
      detailLoading: false,
      applyImage: optImage,
      applyDescription: optDescription,
      applyPrixAchat: optPrixAchat,
      newPrixAchat: p.prixAchat ?? 0,
    }));
    setEnrichData(initial);
    setPhase('recherche');

    // Recherche en parallèle par batch de 3
    for (let i = 0; i < products.length; i += 3) {
      const batch = products.slice(i, i + 3);
      await Promise.all(batch.map(async p => {
        try {
          const res = await api.get(`fournisseur/search?q=${encodeURIComponent(p.name)}`);
          const results: FournisseurResult[] = res.data?.data ?? [];
          updateItem(p.id, { searching: false, results, selected: results[0] ?? null });
        } catch (err: any) {
          updateItem(p.id, { searching: false, searchError: err.message ?? 'Erreur recherche' });
        }
      }));
    }
  }, [products, optImage, optDescription, optPrixAchat, updateItem]);

  // ── Charger la description détaillée d'un résultat ───────────────────────

  const handleSelectResult = async (productId: number, result: FournisseurResult) => {
    updateItem(productId, { selected: result, detailLoading: true, richDescription: result.description ?? '' });
    try {
      if (result.url) {
        const det = await api.get(`fournisseur/detail?url=${encodeURIComponent(result.url)}`);
        if (det.data?.description) {
          updateItem(productId, { richDescription: det.data.description, detailLoading: false });
          return;
        }
      }
    } catch { /* keep existing description */ }
    updateItem(productId, { detailLoading: false });
  };

  // ── Valider l'enrichissement ──────────────────────────────────────────────

  const handleValidate = async () => {
    setValidating(true);
    setPhase('validation');

    const items = enrichData
      .filter(e => e.selected !== null)
      .map(e => ({
        productId: e.product.id,
        updateImage: e.applyImage,
        imageUrl: e.applyImage ? (e.selected!.imageLarge || e.selected!.image) : null,
        updateDescription: e.applyDescription,
        description: e.applyDescription ? (e.richDescription || e.selected!.description) : null,
        updatePrixAchat: e.applyPrixAchat,
        prixAchat: e.applyPrixAchat ? e.newPrixAchat : null,
      }));

    try {
      const res = await api.post('products/bulk-enrich', items);
      setValidationResults(res.data?.results ?? []);
    } catch (err: any) {
      setValidationResults([{ productId: 0, success: false, message: err.message }]);
    } finally {
      setValidating(false);
    }
  };

  const readyCount = enrichData.filter(e => e.selected !== null).length;
  const skippedCount = enrichData.filter(e => e.selected === null && !e.searching).length;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.header}>
          <button onClick={() => navigate('/produits')} className={styles.btnBack}>← Retour</button>
          <h1 className={styles.title}>Enrichissement via fournisseur</h1>
          <span className={styles.subtitle}>{products.length} produit{products.length > 1 ? 's' : ''} sélectionné{products.length > 1 ? 's' : ''}</span>
        </div>

        {/* ── Stepper ── */}
        <div className={styles.stepper}>
          {(['options', 'recherche', 'apercu', 'validation'] as Phase[]).map((p, i) => (
            <div key={p} className={`${styles.step} ${phase === p ? styles.stepActive : ''} ${['options','recherche','apercu','validation'].indexOf(phase) > i ? styles.stepDone : ''}`}>
              <span className={styles.stepNum}>{i + 1}</span>
              <span className={styles.stepLabel}>{{ options: 'Options', recherche: 'Recherche', apercu: 'Aperçu', validation: 'Validation' }[p]}</span>
            </div>
          ))}
        </div>

        {/* ══ PHASE OPTIONS ══ */}
        {phase === 'options' && (
          <div className={styles.optionsPanel}>
            <h2 className={styles.sectionTitle}>Que souhaitez-vous mettre à jour ?</h2>
            <div className={styles.optionsList}>
              <label className={styles.optionRow}>
                <input type="checkbox" checked={optImage} onChange={e => setOptImage(e.target.checked)} />
                <div>
                  <strong>Image</strong>
                  <p>Télécharge l'image du fournisseur et l'envoie automatiquement via FTP</p>
                </div>
              </label>
              <label className={styles.optionRow}>
                <input type="checkbox" checked={optDescription} onChange={e => setOptDescription(e.target.checked)} />
                <div>
                  <strong>Description</strong>
                  <p>Remplace la description par celle du fournisseur</p>
                </div>
              </label>
              <label className={styles.optionRow}>
                <input type="checkbox" checked={optPrixAchat} onChange={e => setOptPrixAchat(e.target.checked)} />
                <div>
                  <strong>Prix d'achat</strong>
                  <p>Met à jour le prix d'achat (PA) depuis le tarif fournisseur</p>
                </div>
              </label>
            </div>
            <div className={styles.optionsFooter}>
              <p className={styles.hint}>La recherche se lance automatiquement sur le site fournisseur pour chaque produit.</p>
              <button
                className={styles.btnPrimary}
                onClick={startRecherche}
                disabled={!optImage && !optDescription && !optPrixAchat}
              >
                Lancer la recherche →
              </button>
            </div>
          </div>
        )}

        {/* ══ PHASE RECHERCHE ══ */}
        {phase === 'recherche' && (
          <div className={styles.recherchePanel}>
            <div className={styles.rechercheHeader}>
              <span>{enrichData.filter(e => !e.searching).length} / {enrichData.length} produits traités</span>
              <div className={styles.rechercheActions}>
                {enrichData.every(e => !e.searching) && (
                  <button className={styles.btnPrimary} onClick={() => setPhase('apercu')}>
                    Passer à l'aperçu ({readyCount} sélectionné{readyCount > 1 ? 's' : ''}) →
                  </button>
                )}
              </div>
            </div>

            {enrichData.map(e => (
              <div key={e.product.id} className={styles.searchCard}>
                <div className={styles.searchCardHeader}>
                  <strong className={styles.productName}>{e.product.name}</strong>
                  {e.searching && <span className={styles.loadingDots}>Recherche en cours…</span>}
                  {e.searchError && <span className={styles.errorText}>{e.searchError}</span>}
                  {!e.searching && !e.searchError && e.results.length === 0 && (
                    <span className={styles.noResults}>Aucun résultat trouvé</span>
                  )}
                  {e.selected && <span className={styles.selectedBadge}>✓ Sélectionné</span>}
                </div>

                {!e.searching && e.results.length > 0 && (
                  <div className={styles.resultsGrid}>
                    {e.results.slice(0, 5).map((r, i) => (
                      <div
                        key={i}
                        className={`${styles.resultCard} ${e.selected === r ? styles.resultCardActive : ''}`}
                        onClick={() => handleSelectResult(e.product.id, r)}
                      >
                        {(r.image || r.imageLarge) && (
                          <img src={r.image || r.imageLarge} alt={r.name} className={styles.resultThumb} />
                        )}
                        <div className={styles.resultInfo}>
                          <span className={styles.resultName}>{r.name}</span>
                          {r.price && <span className={styles.resultPrice}>{r.price}</span>}
                        </div>
                      </div>
                    ))}
                    <div
                      className={`${styles.resultCard} ${styles.resultCardSkip} ${e.selected === null && !e.searching ? styles.resultCardActive : ''}`}
                      onClick={() => updateItem(e.product.id, { selected: null })}
                    >
                      <span>⊘ Ignorer ce produit</span>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {enrichData.every(e => !e.searching) && (
              <div className={styles.rechercheFooter}>
                <button className={styles.btnPrimary} onClick={() => setPhase('apercu')}>
                  Passer à l'aperçu ({readyCount} sélectionné{readyCount > 1 ? 's' : ''}, {skippedCount} ignoré{skippedCount > 1 ? 's' : ''}) →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ══ PHASE APERÇU ══ */}
        {phase === 'apercu' && (
          <div className={styles.apercuPanel}>
            <div className={styles.apercuHeader}>
              <span>{readyCount} produit{readyCount > 1 ? 's' : ''} à mettre à jour</span>
              <button className={styles.btnSecondary} onClick={() => setPhase('recherche')}>← Retour</button>
              <button className={styles.btnPrimary} onClick={handleValidate}>
                ✅ Valider et appliquer →
              </button>
            </div>

            {enrichData.filter(e => e.selected !== null).map(e => {
              const imgSrc = e.selected!.imageLarge || e.selected!.image;
              return (
                <div key={e.product.id} className={styles.apercuCard}>
                  <div className={styles.apercuCardTitle}>
                    <strong>{e.product.name}</strong>
                    <span className={styles.apercuFournisseur}>→ {e.selected!.name}</span>
                  </div>

                  <div className={styles.apercuGrid}>
                    {/* Image */}
                    {optImage && (
                      <div className={styles.apercuSection}>
                        <label className={styles.apercuCheckLabel}>
                          <input type="checkbox" checked={e.applyImage} onChange={ev => updateItem(e.product.id, { applyImage: ev.target.checked })} />
                          <strong>Image</strong>
                        </label>
                        <div className={styles.apercuImageRow}>
                          {e.product.image ? (
                            <div className={styles.apercuImageBox}>
                              <span className={styles.apercuImageLabel}>Actuelle</span>
                              <img src={e.product.image} alt="actuelle" className={styles.apercuThumb} />
                            </div>
                          ) : <div className={styles.apercuImageBox}><span className={styles.apercuImageLabel}>Pas d'image</span></div>}
                          <span className={styles.apercuArrow}>→</span>
                          <div className={styles.apercuImageBox}>
                            <span className={styles.apercuImageLabel}>Nouvelle</span>
                            {imgSrc && <img src={imgSrc} alt="nouvelle" className={styles.apercuThumb} />}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    {optDescription && (
                      <div className={styles.apercuSection}>
                        <label className={styles.apercuCheckLabel}>
                          <input type="checkbox" checked={e.applyDescription} onChange={ev => updateItem(e.product.id, { applyDescription: ev.target.checked })} />
                          <strong>Description</strong>
                        </label>
                        {e.detailLoading ? (
                          <span className={styles.loadingDots}>Chargement…</span>
                        ) : (
                          <textarea
                            className={styles.apercuDescTextarea}
                            value={e.richDescription || e.selected!.description || ''}
                            onChange={ev => updateItem(e.product.id, { richDescription: ev.target.value })}
                          />
                        )}
                      </div>
                    )}

                    {/* Prix d'achat */}
                    {optPrixAchat && (
                      <div className={styles.apercuSection}>
                        <label className={styles.apercuCheckLabel}>
                          <input type="checkbox" checked={e.applyPrixAchat} onChange={ev => updateItem(e.product.id, { applyPrixAchat: ev.target.checked })} />
                          <strong>Prix d'achat</strong>
                        </label>
                        <div className={styles.prixRow}>
                          <span>Actuel : <strong>{e.product.prixAchat?.toFixed(2) ?? '—'} €</strong></span>
                          <span>→</span>
                          <input
                            type="number" step="0.01"
                            value={e.newPrixAchat}
                            onChange={ev => updateItem(e.product.id, { newPrixAchat: parseFloat(ev.target.value) || 0 })}
                            className={styles.prixInput}
                          />
                          <span>€</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            <div className={styles.apercuFooter}>
              <button className={styles.btnPrimary} onClick={handleValidate}>
                ✅ Valider et appliquer ({readyCount} produit{readyCount > 1 ? 's' : ''})
              </button>
            </div>
          </div>
        )}

        {/* ══ PHASE VALIDATION ══ */}
        {phase === 'validation' && (
          <div className={styles.validationPanel}>
            {validating ? (
              <div className={styles.validatingBox}>
                <div className={styles.spinner} />
                <p>Mise à jour en cours… (upload FTP + enregistrement)</p>
              </div>
            ) : (
              <>
                <h2 className={styles.sectionTitle}>Résultats</h2>
                <div className={styles.resultsList}>
                  {validationResults.map((r, i) => {
                    const p = products.find(p => p.id === r.productId);
                    return (
                      <div key={i} className={`${styles.resultRow} ${r.success ? styles.resultRowOk : styles.resultRowErr}`}>
                        <span>{r.success ? '✅' : '❌'}</span>
                        <span className={styles.resultName}>{p?.name ?? `Produit #${r.productId}`}</span>
                        {r.newImageUrl && <img src={r.newImageUrl} alt="" className={styles.resultThumbSmall} />}
                        {r.message && <span className={styles.resultMsg}>{r.message}</span>}
                      </div>
                    );
                  })}
                </div>
                <div className={styles.validationFooter}>
                  <span>
                    {validationResults.filter(r => r.success).length} succès,{' '}
                    {validationResults.filter(r => !r.success).length} erreur(s)
                  </span>
                  <button className={styles.btnPrimary} onClick={() => navigate('/produits')}>
                    ← Retour aux produits
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};
